// Mirrors api `src/round-settings/*.dto.ts`, `round-preview.dto.ts` and `round-rebuild.dto.ts`.

export enum RoundMatchMode {
  STAGE = 'STAGE',
  STAGE_AND_DATE = 'STAGE_AND_DATE',
  DATE = 'DATE',
}

/** What the accounting ledger may do with rounds. */
export enum LedgerRole {
  CREATE = 'CREATE',
  TAG = 'TAG',
  IGNORE = 'IGNORE',
}

/** What Aumni, deal-doc extraction and Doc Hub may do. */
export enum DocsRole {
  ENRICH_ONLY = 'ENRICH_ONLY',
  CREATE = 'CREATE',
}

/** Who wins when both sources may create. */
export enum Precedence {
  LEDGER_LEADS = 'LEDGER_LEADS',
  DOCS_LEAD = 'DOCS_LEAD',
}

/** Which round a transaction joins when several securities land in the same window. */
export enum RoundAssignmentMode {
  MATCH_STAGE = 'MATCH_STAGE',
  PREFERRED = 'PREFERRED',
}

/** Security kinds a round can be anchored by; the options come from the API. */
export enum RoundSecurityType {
  PREFERRED = 'PREFERRED',
  COMMON = 'COMMON',
  NOTE = 'NOTE',
  SAFE = 'SAFE',
  WARRANT = 'WARRANT',
  TOKEN = 'TOKEN',
  UNIT = 'UNIT',
  SECONDARY = 'SECONDARY',
  OTHER = 'OTHER',
}

/** How far a settings change reaches. One rebuild, three starting points. */
export enum RebuildScope {
  GO_FORWARD = 'GO_FORWARD',
  UNEDITED_SYSTEM = 'UNEDITED_SYSTEM',
  ALL = 'ALL',
}

export interface RoundCreationSettingsUpdate {
  roundCreationMatchMode: RoundMatchMode;
  roundCreationWindowDays: number | null;
  transactionAssignmentMatchMode: RoundMatchMode;
  transactionAssignmentWindowDays: number | null;
  /** Merge-on-omit server-side; the page always sends it. Explicit null is rejected. */
  unassignedGraceDays?: number;
  /** Null hands the dial back to the client's legacy configuration. */
  ledgerRole?: LedgerRole | null;
  docsRole?: DocsRole | null;
  /** Null means inferred from whether the client uses Deal Intelligence. */
  precedence?: Precedence | null;
  conversionsCreateRounds?: boolean;
  assignmentMode?: RoundAssignmentMode;
  /** Null means every kind may create a round. */
  securityTypes?: RoundSecurityType[] | null;
}

export type RoundCreationSettingsDefaults = Required<RoundCreationSettingsUpdate>;

export interface RoundCreationSettingsResponse extends RoundCreationSettingsDefaults {
  /** False when the singleton row is missing; ingestion halts in that state, so the page warns. */
  seeded: boolean;
  settingsVersion: number;
  updatedBy: string | null;
  updatedAt: string | null;
  /**
   * What the two nullable dials resolve to right now, so the page can show what is actually in
   * force without reimplementing the fallback. Null when no settings row exists.
   */
  effective: {
    ledgerRole: LedgerRole;
    ledgerRoleFromPanel: boolean;
    docsRole: DocsRole;
    docsRoleFromPanel: boolean;
    /** The client's existing round provider, which governs while docsRole is unchosen. */
    legacyRoundProvider: string | null;
    precedence: Precedence;
    precedenceFromPanel: boolean;
    /** Both legacy booleans set: four states collapsing into three, and a human should decide. */
    legacyLedgerAmbiguous: boolean;
  } | null;

  /** Reset-to-defaults reads these, so the values never fork into frontend source. */
  defaults: RoundCreationSettingsDefaults;
}

export type PreviewExclusionReason =
  | 'assignment-disabled'
  | 'conversions-do-not-create'
  | 'not-round-eligible'
  | 'fund-level'
  | 'pendingAssignment'
  | 'unassigned';

export type PreviewEmptyState =
  'noEligibleTransactions' | 'creationDisabled' | 'assignmentDisabled' | 'companyNotFound';

export interface RoundPreviewRequest {
  companyId: number;
  scope: RebuildScope;
  settings: RoundCreationSettingsUpdate;
}

/** One member transaction of a previewed round, enough for the drill-down to stand on its own. */
export interface PreviewMember {
  transactionId: number;
  transactionDate: string | null;
  transType: string | null;
  position: string | null;
  fundId: number | null;
  amount: number | null;
  shares: number | null;
  /** True when the row joined a round it did not create: the assignment rule at work. */
  attached: boolean;
}

export interface PreviewRound {
  /** `existing:<id>` for a kept round, `new:<k>` for one the plan would mint. */
  key: string;
  existing: boolean;
  name: string | null;
  roundDate: string | null;
  stageKey: number | null;
  stageName: string | null;
  /** Only a kept round carries a raise; a planned one has no source for it yet. */
  raiseAmount: number | null;
  /** Sums over the member transactions. */
  investmentAmount: number;
  shares: number;
  transactionIds: number[];
  transactions: PreviewMember[];
}

/**
 * A round that exists today with no counterpart under the drafted rules. The delta says how many
 * disappear; this says which, and where the money in them ends up.
 */
export interface PreviewRemovedRound {
  key: string;
  name: string | null;
  roundDate: string | null;
  stageName: string | null;
  transactionCount: number;
  investmentAmount: number;
  /** Names of the rounds its transactions land in instead. */
  movedTo: string[];
  /** True when nothing it holds finds a round at all: a loss rather than a merge. */
  everythingLeftOut: boolean;
}

export interface PreviewTransaction {
  transactionId: number;
  reason: PreviewExclusionReason;
  position: string | null;
  transactionDate: string | null;
  transType: string | null;
  amount: number | null;
}

export interface RoundPreviewResponse {
  companyId: number;
  scope: RebuildScope;
  settings: RoundCreationSettingsUpdate;
  currentRoundCount: number;
  plannedRoundCount: number;
  rounds: PreviewRound[];
  removed: PreviewRemovedRound[];
  unassigned: PreviewTransaction[];
  excluded: PreviewTransaction[];
  wipedRoundCount: number;
  handEditedWipedCount: number;
  protectedForMissingProvenanceCount: number;
  emptyState: PreviewEmptyState | null;
}

export interface RoundRebuildRequest {
  scope: RebuildScope;
  settings: RoundCreationSettingsUpdate;
  /** ALL only: the hand-edited count the caller has seen and agrees to wipe. Never remembered. */
  confirmHandEditedWipe?: number;
  companyIds?: number[];
}

export interface CompanyRebuildReport {
  companyId: number;
  roundsBefore: number;
  roundsAfter: number;
  roundsWiped: number;
  handEditedWiped: number;
  protectedForMissingProvenance: number;
  assigned: number;
  created: number;
  pendingAssignment: number;
  unassigned: number;
  carriedForward: number;
  roundsWithoutReplacement: number;
  documentsReattached: number;
  documentsNeedingReview: number;
  errors: string[];
}

export interface RoundRebuildResponse {
  scope: RebuildScope;
  settingsVersion: number;
  handEditedWiped: number;
  companies: CompanyRebuildReport[];
  auditId: number | null;
}
